import React, { useId, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  LocalPermissionsSnapshot,
  visibleLocalPermissions,
} from '../lib/local-permissions';

export function LocalPermissionsPanel({
  snapshot,
}: {
  snapshot: LocalPermissionsSnapshot;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const permissions = visibleLocalPermissions(snapshot.value);
  const states = permissions.map(({ state }) => state);
  if (!permissions.length) return null;
  const summary = states.every((state) => state === 'Unknown')
    ? 'Permission data unavailable'
    : [
        ['Prohibited', 'restricted'],
        ['Permitted', 'permitted'],
        ['Unknown', 'unknown'],
      ]
        .map(([state, label]) => {
          const count = states.filter((value) => value === state).length;
          return count ? `${count} ${label}` : null;
        })
        .filter(Boolean)
        .join(' · ');
  return (
    <Box
      component="section"
      aria-label="In-unit wall controller permissions"
      sx={{ borderTop: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75 }}
    >
      <Button
        fullWidth
        color="inherit"
        aria-expanded={expanded}
        aria-controls={detailsId}
        onClick={() => setExpanded((current) => !current)}
        endIcon={
          <ExpandMoreIcon
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }}
          />
        }
        sx={{
          justifyContent: 'space-between',
          textAlign: 'left',
          textTransform: 'none',
          px: 0,
          py: 0.5,
        }}
      >
        <Box component="span">
          <Typography variant="subtitle2" component="span" display="block">
            In-unit wall controller permissions
          </Typography>
          <Typography
            variant="caption"
            component="span"
            display="block"
            color="text.secondary"
          >
            {summary}
          </Typography>
        </Box>
      </Button>
      {snapshot.pending && (
        <Typography component="p" variant="caption" role="status">
          Permission change pending confirmation.
        </Typography>
      )}
      <Collapse in={expanded} unmountOnExit id={detailsId}>
        <Typography
          component="p"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Reported restrictions for this logical group.
        </Typography>
        <Table
          size="small"
          aria-label="Reported local permissions"
          sx={{ '& th, & td': { px: 0, py: 0.5 } }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Function</TableCell>
              <TableCell align="right">Reported restriction</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map(({ field, label, state }) => (
              <TableRow key={field}>
                <TableCell component="th" scope="row">
                  {label}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color:
                      state === 'Prohibited' ? '#b3261e' : 'text.secondary',
                  }}
                >
                  {state}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography
          component="p"
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          Permitted means this central restriction is clear. Local settings may
          still limit access. Unknown means the controller has not reported this
          permission.
        </Typography>
        <Typography component="p" variant="caption" color="text.secondary">
          Last complete observation:{' '}
          {snapshot.observedAt != null &&
          Number.isFinite(new Date(snapshot.observedAt).getTime())
            ? new Date(snapshot.observedAt).toLocaleString()
            : 'Unavailable'}
        </Typography>
      </Collapse>
    </Box>
  );
}
